/**
 * Filter string builder for Algolia searches.
 *
 * Converts our FilterState to Algolia filter syntax.
 * Handles product configurator timestamps for release scheduling.
 * Supports ID-based filtering with facet value lookup.
 *
 * ## Disjunctive Faceting:
 *
 * For OR filters with proper facet counts, we split into:
 * - `buildBaseFilterString()` - Returns filters string for non-facet filters
 * - `buildFacetFilters()` - Returns facetFilters array for disjunctive facets
 *
 * This ensures facet counts show "what you'd get if you added this option"
 * rather than "what's left after filtering".
 */

import { devError } from '@/lib/utils/devLog';
import { logAlgoliaRequest, logAlgoliaResponse } from './debugLog';
import { findFacetValueById } from './facetValues';
import type { FacetResults, FilterState } from './types';

/**
 * Product type for filtering.
 */
export type ProductType = 'Watch' | 'Strap' | 'Eyewear';

/**
 * Options for building filter strings.
 */
export interface BuildFilterOptions extends FilterState {
  /** Product type to filter by */
  productType?: ProductType;

  /** Enable draft/preview mode */
  draftMode?: boolean;

  /** Locale code for locale-specific attributes (e.g., 'EN_US') */
  localeCode?: string;

  /** Additional custom filter string to append */
  customFilter?: string;

  /**
   * Facet results for ID to full value lookup.
   * When provided, filter values are treated as IDs and looked up.
   */
  facetLookup?: FacetResults;

  /**
   * Filter context for product configurator timestamps.
   * Default: 'plp' for product listing pages.
   * Use 'search' for search overlay.
   */
  filterContext?: ProductConfigurationFilterType;
}

/**
 * Build an Algolia filter string from filter state.
 *
 * When facetLookup is provided, filter values are treated as IDs
 * and translated to full Algolia values using the facet results.
 *
 * @param options - Filter options
 * @returns Algolia filter string
 *
 * @example
 * buildFilterString({
 *   productType: 'Watch',
 *   collection: ['navitimer'],
 *   size: ['41', '43'],
 *   localeCode: 'EN_US',
 * })
 * // Returns: "product_type:Watch AND (collection:'navitimer') AND (size:41 OR size:43)"
 */
export function buildFilterString(options: BuildFilterOptions): string {
  const {
    productType,
    draftMode = false,
    localeCode = 'EN_US',
    sku,
    // Watch filters
    collection,
    size,
    case: caseFilter,
    dialColor,
    type,
    // Watch bracelet category filters (singular pipe-format)
    braceletMaterial,
    braceletColor,
    // Strap filters
    material,
    color,
    lug,
    lugType,
    buckle,
    // Eyewear filters
    frameMaterial,
    lensColor,
    // Common filters
    priceMin,
    priceMax,
    customFilter,
    facetLookup,
    filterContext = ProductConfigurationFilterType.PLP,
  } = options;

  const filters: string[] = [];

  // SKU/objectID filter (exact match)
  if (sku) {
    filters.push(`objectID:${sku}`);
  }

  // Helper to translate ID to full value using facet lookup
  const translateId = (id: string, facetBaseName: string, facetAttr: string): string => {
    if (!facetLookup) return id;

    // Try to find the full value in facet results
    const facetValues = facetLookup[facetAttr];
    if (facetValues) {
      const fullValue = findFacetValueById(id, facetValues, facetBaseName);
      if (fullValue) return fullValue;
    }

    // Fallback to ID if not found
    return id;
  };

  // Product type filter
  if (productType) {
    filters.push(`product_type:${productType}`);
  }

  // Product configurator filters (release/expire timestamps)
  const configFilters = buildProductConfiguratorFilters(draftMode, filterContext);
  if (configFilters) {
    filters.push(configFilters);
  }

  // Collection filter - uses OR logic (show products in ANY selected collection)
  // Example: collection=Navitimer,Superocean → show Navitimer OR Superocean watches
  if (collection && collection.length > 0) {
    const collectionFilter = collection
      .map((c) => {
        const fullValue = facetLookup ? translateId(c, 'collection', 'collection') : c;
        return `collection:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${collectionFilter})`);
  }

  // Size filter - uses AND logic (products must match ALL selected sizes)
  // Note: In practice, a watch has one size, so AND means "show only if size is in list"
  // For multi-size products, this would require all sizes to be present
  if (size && size.length > 0) {
    // For size, we still use OR since a product has a single size value
    // AND logic here would mean "product must have size 42 AND size 46" which is impossible
    const sizeFilter = size.map((s) => `size:${s}`).join(' OR ');
    filters.push(`(${sizeFilter})`);
  }

  // Case filter - uses OR logic (show products matching ANY selected case material)
  // NOTE: AND logic infrastructure exists if needed in future (change to ' AND ')
  if (caseFilter && caseFilter.length > 0) {
    const caseAttr = `case.${localeCode}`;
    const caseFilterStr = caseFilter
      .map((c) => {
        const fullValue = translateId(c, 'case', caseAttr);
        return `${caseAttr}:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${caseFilterStr})`);
  }

  // Dial color filter - uses OR logic (show products matching ANY selected color)
  // NOTE: AND logic infrastructure exists if needed in future (change to ' AND ')
  if (dialColor && dialColor.length > 0) {
    const dialAttr = `dial_color.${localeCode}`;
    const dialFilterStr = dialColor
      .map((c) => {
        const fullValue = translateId(c, 'dial_color', dialAttr);
        return `${dialAttr}:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${dialFilterStr})`);
  }

  // Type filter - uses OR logic (show products matching ANY selected type)
  // NOTE: AND logic infrastructure exists if needed in future (change to ' AND ')
  // Example with AND: type=Limited+Gold → show watches that are Limited Edition AND Gold
  if (type && type.length > 0) {
    const typeAttr = `type.${localeCode}`;
    const typeFilterStr = type
      .map((t) => {
        const fullValue = translateId(t, 'type', typeAttr);
        return `${typeAttr}:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${typeFilterStr})`);
  }

  // Bracelet material category (singular pipe-format, BEC-24331)
  if (braceletMaterial && braceletMaterial.length > 0) {
    const attr = `bracelet_category_material.${localeCode}`;
    const filterStr = braceletMaterial
      .map((m) => {
        const fullValue = translateId(m, 'bracelet_category_material', attr);
        return `${attr}:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${filterStr})`);
  }

  // Bracelet color category (singular pipe-format, BEC-24331)
  if (braceletColor && braceletColor.length > 0) {
    const attr = `bracelet_category_color.${localeCode}`;
    const filterStr = braceletColor
      .map((c) => {
        const fullValue = translateId(c, 'bracelet_category_color', attr);
        return `${attr}:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${filterStr})`);
  }

  // ============================================
  // Strap filters
  // ============================================

  // Material filter - uses OR logic
  if (material && material.length > 0) {
    const materialAttr = `material.${localeCode}`;
    const materialFilterStr = material
      .map((m) => {
        const fullValue = translateId(m, 'material', materialAttr);
        return `${materialAttr}:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${materialFilterStr})`);
  }

  // Color filter (straps) - uses OR logic
  if (color && color.length > 0) {
    const colorAttr = `color.${localeCode}`;
    const colorFilterStr = color
      .map((c) => {
        const fullValue = translateId(c, 'color', colorAttr);
        return `${colorAttr}:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${colorFilterStr})`);
  }

  // Lug width filter - uses OR logic
  // Note: lug_size is locale-independent (numeric string like "24")
  if (lug && lug.length > 0) {
    const lugFilterStr = lug.map((l) => `lug_size:${l}`).join(' OR ');
    filters.push(`(${lugFilterStr})`);
  }

  // Lug type filter - uses OR logic
  if (lugType && lugType.length > 0) {
    const lugTypeAttr = `lug_type.${localeCode}`;
    const lugTypeFilterStr = lugType
      .map((lt) => {
        const fullValue = translateId(lt, 'lug_type', lugTypeAttr);
        return `${lugTypeAttr}:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${lugTypeFilterStr})`);
  }

  // Buckle filter - uses OR logic
  if (buckle && buckle.length > 0) {
    const buckleAttr = `buckle_type.${localeCode}`;
    const buckleFilterStr = buckle
      .map((b) => {
        const fullValue = translateId(b, 'buckle_type', buckleAttr);
        return `${buckleAttr}:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${buckleFilterStr})`);
  }

  // ============================================
  // Eyewear filters
  // ============================================

  // Frame material filter - uses OR logic
  if (frameMaterial && frameMaterial.length > 0) {
    const frameMaterialAttr = `frame_material.${localeCode}`;
    const frameMaterialFilterStr = frameMaterial
      .map((fm) => {
        const fullValue = translateId(fm, 'frame_material', frameMaterialAttr);
        return `${frameMaterialAttr}:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${frameMaterialFilterStr})`);
  }

  // Lens color filter - uses OR logic
  if (lensColor && lensColor.length > 0) {
    const lensColorAttr = `lens_color.${localeCode}`;
    const lensColorFilterStr = lensColor
      .map((lc) => {
        const fullValue = translateId(lc, 'lens_color', lensColorAttr);
        return `${lensColorAttr}:'${escapeFilterValue(fullValue)}'`;
      })
      .join(' OR ');
    filters.push(`(${lensColorFilterStr})`);
  }

  // Price range
  if (priceMin !== undefined || priceMax !== undefined) {
    const min = priceMin ?? 0;
    const max = priceMax ?? 999999999;
    filters.push(`price:${min} TO ${max}`);
  }

  // Custom filter
  if (customFilter) {
    filters.push(customFilter);
  }

  return filters.join(' AND ');
}

/**
 * Build base filter string for non-facet filters.
 *
 * This returns only the filters that should NOT be disjunctive:
 * - product_type
 * - timestamps (product configurator)
 * - price range
 * - SKU
 * - custom filters
 *
 * Use with `buildFacetFilters()` for proper disjunctive faceting.
 */
export function buildBaseFilterString(options: BuildFilterOptions): string {
  const {
    productType,
    draftMode = false,
    sku,
    priceMin,
    priceMax,
    customFilter,
    filterContext = ProductConfigurationFilterType.PLP,
  } = options;

  const filters: string[] = [];

  // SKU/objectID filter (exact match)
  if (sku) {
    filters.push(`objectID:${sku}`);
  }

  // Product type filter
  if (productType) {
    filters.push(`product_type:${productType}`);
  }

  // Product configurator filters (release/expire timestamps)
  const configFilters = buildProductConfiguratorFilters(draftMode, filterContext);
  if (configFilters) {
    filters.push(configFilters);
  }

  // Price range
  if (priceMin !== undefined || priceMax !== undefined) {
    const min = priceMin ?? 0;
    const max = priceMax ?? 999999999;
    filters.push(`price:${min} TO ${max}`);
  }

  // Custom filter
  if (customFilter) {
    filters.push(customFilter);
  }

  return filters.join(' AND ');
}

/**
 * Facet filter type for Algolia.
 * Each inner array is OR'd, outer arrays are AND'd.
 */
export type FacetFilterArray = Array<string | string[]>;

/**
 * Build facet filters for disjunctive faceting.
 *
 * Returns an array where each element is either:
 * - A string for AND filters
 * - An array of strings for OR filters (disjunctive)
 *
 * For OR logic, each facet type gets its own inner array.
 * This tells Algolia to calculate counts as if that facet wasn't filtered.
 *
 * @example
 * // With type=["Men", "Women"] and collection=["Navitimer"]
 * // Returns: [["type.EN_US:'Men'", "type.EN_US:'Women'"], ["collection:'Navitimer'"]]
 * // This means: (Men OR Women) AND (Navitimer)
 * // And facet counts for type show all available types, not filtered by current selection
 */
export function buildFacetFilters(options: BuildFilterOptions): FacetFilterArray {
  const {
    localeCode = 'EN_US',
    // Watch filters
    collection,
    size,
    case: caseFilter,
    dialColor,
    type,
    // Watch bracelet category filters (singular pipe-format)
    braceletMaterial,
    braceletColor,
    // Strap filters
    material,
    color,
    lug,
    lugType,
    buckle,
    // Eyewear filters
    frameMaterial,
    lensColor,
    facetLookup,
  } = options;

  const facetFilters: FacetFilterArray = [];

  // Helper to translate ID to full value using facet lookup
  const translateId = (id: string, facetBaseName: string, facetAttr: string): string => {
    if (!facetLookup) return id;

    const facetValues = facetLookup[facetAttr];
    if (facetValues) {
      const fullValue = findFacetValueById(id, facetValues, facetBaseName);
      if (fullValue) return fullValue;
    }

    return id;
  };

  // Collection filter - disjunctive (OR within, shows all collection counts)
  if (collection && collection.length > 0) {
    const collectionFilters = collection.map((c) => {
      const fullValue = facetLookup ? translateId(c, 'collection', 'collection') : c;
      return `collection:${escapeFilterValue(fullValue)}`;
    });
    facetFilters.push(collectionFilters);
  }

  // Size filter - disjunctive
  if (size && size.length > 0) {
    const sizeFilters = size.map((s) => `size:${s}`);
    facetFilters.push(sizeFilters);
  }

  // Case filter - disjunctive
  if (caseFilter && caseFilter.length > 0) {
    const caseAttr = `case.${localeCode}`;
    const caseFilters = caseFilter.map((c) => {
      const fullValue = translateId(c, 'case', caseAttr);
      return `${caseAttr}:${escapeFilterValue(fullValue)}`;
    });
    facetFilters.push(caseFilters);
  }

  // Dial color filter - disjunctive
  if (dialColor && dialColor.length > 0) {
    const dialAttr = `dial_color.${localeCode}`;
    const dialFilters = dialColor.map((c) => {
      const fullValue = translateId(c, 'dial_color', dialAttr);
      return `${dialAttr}:${escapeFilterValue(fullValue)}`;
    });
    facetFilters.push(dialFilters);
  }

  // Type filter - disjunctive
  if (type && type.length > 0) {
    const typeAttr = `type.${localeCode}`;
    const typeFilters = type.map((t) => {
      const fullValue = translateId(t, 'type', typeAttr);
      return `${typeAttr}:${escapeFilterValue(fullValue)}`;
    });
    facetFilters.push(typeFilters);
  }

  // Bracelet material category - disjunctive (singular pipe-format, BEC-24331)
  if (braceletMaterial && braceletMaterial.length > 0) {
    const attr = `bracelet_category_material.${localeCode}`;
    facetFilters.push(
      braceletMaterial.map((m) => {
        const fullValue = translateId(m, 'bracelet_category_material', attr);
        return `${attr}:${escapeFilterValue(fullValue)}`;
      })
    );
  }

  // Bracelet color category - disjunctive (singular pipe-format, BEC-24331)
  if (braceletColor && braceletColor.length > 0) {
    const attr = `bracelet_category_color.${localeCode}`;
    facetFilters.push(
      braceletColor.map((c) => {
        const fullValue = translateId(c, 'bracelet_category_color', attr);
        return `${attr}:${escapeFilterValue(fullValue)}`;
      })
    );
  }

  // ============================================
  // Strap filters - disjunctive
  // ============================================

  // Material filter - disjunctive
  if (material && material.length > 0) {
    const materialAttr = `material.${localeCode}`;
    const materialFilters = material.map((m) => {
      const fullValue = translateId(m, 'material', materialAttr);
      return `${materialAttr}:${escapeFilterValue(fullValue)}`;
    });
    facetFilters.push(materialFilters);
  }

  // Color filter - disjunctive
  if (color && color.length > 0) {
    const colorAttr = `color.${localeCode}`;
    const colorFilters = color.map((c) => {
      const fullValue = translateId(c, 'color', colorAttr);
      return `${colorAttr}:${escapeFilterValue(fullValue)}`;
    });
    facetFilters.push(colorFilters);
  }

  // Lug width filter - disjunctive
  // Note: lug_size is locale-independent (numeric string like "24")
  if (lug && lug.length > 0) {
    const lugFilters = lug.map((l) => `lug_size:${l}`);
    facetFilters.push(lugFilters);
  }

  // Lug type filter - disjunctive
  if (lugType && lugType.length > 0) {
    const lugTypeAttr = `lug_type.${localeCode}`;
    const lugTypeFilters = lugType.map((lt) => {
      const fullValue = translateId(lt, 'lug_type', lugTypeAttr);
      return `${lugTypeAttr}:${escapeFilterValue(fullValue)}`;
    });
    facetFilters.push(lugTypeFilters);
  }

  // Buckle filter - disjunctive
  if (buckle && buckle.length > 0) {
    const buckleAttr = `buckle_type.${localeCode}`;
    const buckleFilters = buckle.map((b) => {
      const fullValue = translateId(b, 'buckle_type', buckleAttr);
      return `${buckleAttr}:${escapeFilterValue(fullValue)}`;
    });
    facetFilters.push(buckleFilters);
  }

  // ============================================
  // Eyewear filters - disjunctive
  // ============================================

  // Frame material filter - disjunctive
  if (frameMaterial && frameMaterial.length > 0) {
    const frameMaterialAttr = `frame_material.${localeCode}`;
    const frameMaterialFilters = frameMaterial.map((fm) => {
      const fullValue = translateId(fm, 'frame_material', frameMaterialAttr);
      return `${frameMaterialAttr}:${escapeFilterValue(fullValue)}`;
    });
    facetFilters.push(frameMaterialFilters);
  }

  // Lens color filter - disjunctive
  if (lensColor && lensColor.length > 0) {
    const lensColorAttr = `lens_color.${localeCode}`;
    const lensColorFilters = lensColor.map((lc) => {
      const fullValue = translateId(lc, 'lens_color', lensColorAttr);
      return `${lensColorAttr}:${escapeFilterValue(fullValue)}`;
    });
    facetFilters.push(lensColorFilters);
  }

  return facetFilters;
}

/**
 * Check if filter state has any ID-based filters that need lookup.
 */
export function hasIdFilters(filters: FilterState): boolean {
  return !!(
    // Watch filters
    (
      filters.case?.length ||
      filters.dialColor?.length ||
      filters.type?.length ||
      filters.braceletMaterial?.length ||
      filters.braceletColor?.length ||
      // Strap filters
      filters.material?.length ||
      filters.color?.length ||
      filters.lugType?.length ||
      filters.buckle?.length ||
      // Eyewear filters
      filters.frameMaterial?.length ||
      filters.lensColor?.length
    )
  );
}

/**
 * Build product configurator timestamp filters.
 *
 * The product configurator uses release/expire timestamps to control
 * when products appear. Different contexts use different timestamp fields:
 * - 'plp': plp_release_ts / plp_expire_ts (product listing pages)
 * - 'search': search_release_ts / search_expire_ts (search overlay)
 * - 'components': components_release_ts / components_expire_ts
 *
 * @param draftMode - Whether to use preview mode
 * @param filterType - The context for filtering (default: 'plp')
 * @returns Filter string or null if not using product configurator
 */
function buildProductConfiguratorFilters(
  draftMode: boolean,
  filterType: ProductConfigurationFilterType = ProductConfigurationFilterType.PLP
): string | null {
  const useProductConfigurator = process.env.NEXT_PUBLIC_USE_PRODUCT_CONFIGURATOR === 'true';

  if (!useProductConfigurator) {
    return null;
  }

  const now = Math.floor(Date.now() / 1000);
  const prefix = filterType;

  if (draftMode) {
    // In preview mode, show both released products AND preview-flagged products.
    // Uses distributive OR so products within their release window still appear
    // alongside products that only have the preview flag set.
    return `(${prefix}_release_ts < ${now} OR ${prefix}_preview:true) AND (${prefix}_expire_ts > ${now} OR ${prefix}_preview:true)`;
  }

  // In production, only show products within their release window
  return `${prefix}_release_ts < ${now} AND ${prefix}_expire_ts > ${now}`;
}

/**
 * Escape special characters in filter values.
 *
 * Algolia filter values need quotes escaped.
 */
function escapeFilterValue(value: string): string {
  return value.replace(/'/g, "\\'").replace(/"/g, '\\"');
}

/**
 * Build a filter for fetching specific SKUs.
 *
 * @param skus - Array of SKUs to fetch
 * @returns Filter string
 */
export function buildSkuFilter(skus: string[]): string {
  if (skus.length === 0) {
    return '';
  }

  if (skus.length === 1) {
    return `objectID:${skus[0]}`;
  }

  const skuFilters = skus.map((sku) => `objectID:${sku}`).join(' OR ');
  return `(${skuFilters})`;
}

/**
 * Build a filter for a specific collection.
 *
 * @param collectionSlug - Collection slug
 * @returns Filter string
 */
export function buildCollectionFilter(collectionSlug: string): string {
  return `collection:'${escapeFilterValue(collectionSlug)}'`;
}

/**
 * Product configuration filter type.
 * Determines which timestamp fields to use for release filtering.
 */
export enum ProductConfigurationFilterType {
  PLP = 'plp',
  PDP = 'pdp',
  SEARCH = 'search',
  COMPONENTS = 'components',
}

/**
 * Filter products by release status using Algolia.
 *
 * Fetches products by SKU via getObjects (no search quota), then
 * post-filters using the specified context timestamps
 * (e.g. `components_release_ts` / `components_expire_ts`).
 *
 * @param skus - Array of SKUs to check
 * @param locale - Locale code for index resolution
 * @param productType - Optional product type to restrict results
 * @param filterType - Which timestamp context to check (plp, pdp, search, components)
 * @param indexName - Algolia index name (defaults to config)
 * @param isSsr - Whether running on server
 * @param draftMode - Whether in draft/preview mode
 * @returns Filtered product hits
 */
export async function getFilterReleasedProducts<T extends { objectID: string }>(
  skus: string[],
  locale: string,
  productType?: ProductType,
  filterType: ProductConfigurationFilterType = ProductConfigurationFilterType.PLP,
  indexName?: string,
  isSsr: boolean = false,
  draftMode?: boolean
): Promise<T[]> {
  if (skus.length === 0) return [];

  try {
    const { getAlgoliaClient } = await import('./client');
    const { ALGOLIA_CONFIG } = await import('./config');

    const client = getAlgoliaClient({ ssr: isSsr });
    const index = indexName || ALGOLIA_CONFIG.getProductIndex(locale);

    logAlgoliaRequest('getFilterReleasedProducts', {
      indexName: index,
      environment: ALGOLIA_CONFIG.environment,
      locale,
      productType: productType ?? null,
      filterType,
      draftMode: draftMode ?? false,
      skus,
      count: skus.length,
    });

    const response = await client.getObjects<T>({
      requests: skus.map((sku) => ({ indexName: index, objectID: sku })),
    });
    const results = response.results.filter((item): item is T => item !== null);

    const filtered = filterReleasedOrDraftProducts(results, filterType, productType, draftMode);

    logAlgoliaResponse('getFilterReleasedProducts', {
      indexName: index,
      requested: skus.length,
      fetched: results.length,
      released: filtered.length,
      filteredOut: results.length - filtered.length,
      objectIds: filtered.map((item) => item.objectID),
    });

    return filtered;
  } catch (error) {
    devError('[Algolia] Failed to fetch product configurations:', {
      skus,
      indexName,
      error: error instanceof Error ? error.message : error,
    });
    return [];
  }
}

/**
 * Post-fetch filter for products retrieved via getObjects.
 *
 * Uses the specified context's timestamp fields to determine visibility.
 * Matches the legacy breitling-storefront-next `filterReleasedOrDraftProducts`.
 *
 * @param hits - Raw Algolia hits
 * @param filterType - Which timestamp context to check
 * @param productType - Optional product type restriction
 * @param draftMode - Whether to include preview-flagged products
 * @returns Filtered hits within their release window (or with preview flag in draft mode)
 */
export function filterReleasedOrDraftProducts<T extends { objectID: string }>(
  hits: T[],
  filterType: ProductConfigurationFilterType = ProductConfigurationFilterType.PLP,
  productType?: ProductType,
  draftMode?: boolean
): T[] {
  const useProductConfigurator = process.env.NEXT_PUBLIC_USE_PRODUCT_CONFIGURATOR === 'true';

  if (!useProductConfigurator) {
    return productType
      ? hits.filter((hit) => {
          // oxlint-disable-next-line typescript/no-explicit-any
          const hitProductType = (hit as any).product_type as string | undefined;
          return !hitProductType || hitProductType === productType;
        })
      : hits;
  }

  const now = Math.floor(Date.now() / 1000);
  const releaseKey = `${filterType}_release_ts`;
  const expireKey = `${filterType}_expire_ts`;
  const previewKey = `${filterType}_preview`;

  return hits.filter((hit) => {
    // oxlint-disable-next-line typescript/no-explicit-any
    const record = hit as any;

    if (productType && record.product_type && record.product_type !== productType) {
      return false;
    }

    const isReleased =
      (!(releaseKey in record) || record[releaseKey] < now) &&
      (!(expireKey in record) || record[expireKey] > now);

    const isPreview = draftMode && record[previewKey];

    return isReleased || isPreview;
  });
}
